import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode
} from 'react';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  // Role removed - use permission templates instead
  department?: string;
  position?: string;
  employeeId?: string;
  pointsBalance?: number;
  lastLogin?: string;
  approvalStatus?: string;
  emailVerified?: boolean;
  workPhone?: string;
  phoneNumber?: string;
  contactsVisible?: boolean;
  etatas?: number;
  passwordResetRequired?: boolean;
  canApproveSchedules?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  permissions: string[];
  /** null = all departments (view_all / *.*); otherwise allowed view departments */
  scheduleViewDepartments: string[] | null;
  canViewAllScheduleDepartments: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<{ success: boolean; needsApproval: boolean }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (...permissionCodes: string[]) => boolean;
  loading: boolean;
  error: string | null;
  setTokenFromServer: (newToken: string) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  department?: string;
  position?: string;
  // Role removed - use permission templates instead
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const VISIBILITY_RECHECK_DEBOUNCE_MS = 30000;

/** Skip forcing logout for these auth endpoints (bad credentials should not clear session). */
function isAuthCredentialEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register')
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [permissions, setPermissions] = useState<string[]>([]);
  const [scheduleViewDepartments, setScheduleViewDepartments] = useState<string[] | null>([]);
  const [canViewAllScheduleDepartments, setCanViewAllScheduleDepartments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const logoutInProgressRef = useRef(false);
  const lastVisibilityCheckRef = useRef(0);

  const applyAuthHeader = useCallback((nextToken: string | null) => {
    if (nextToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const forceLogout = useCallback(() => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;

    localStorage.removeItem('token');
    applyAuthHeader(null);
    setToken(null);
    setUser(null);
    setPermissions([]);
    setScheduleViewDepartments([]);
    setCanViewAllScheduleDepartments(false);
    setError(null);

    // Allow a later login / logout after concurrent 401s settle
    window.setTimeout(() => {
      logoutInProgressRef.current = false;
    }, 500);
  }, [applyAuthHeader]);

  // Keep axios default header in sync with token state
  useEffect(() => {
    applyAuthHeader(token);
  }, [token, applyAuthHeader]);

  // Global 401 → session expired / invalid token → login screen
  // Only when the request actually sent a Bearer token (avoids wiping a fresh login
  // if a call raced before the Authorization header was set).
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (err) => {
        const status = err?.response?.status;
        const requestUrl = err?.config?.url as string | undefined;
        const headers = err?.config?.headers || {};
        const authHeader = headers.Authorization || headers.authorization;
        const hadBearer =
          typeof authHeader === 'string' && authHeader.toLowerCase().startsWith('bearer ');

        if (status === 401 && hadBearer && !isAuthCredentialEndpoint(requestUrl)) {
          forceLogout();
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [forceLogout]);

  // Re-validate session when user returns to the tab
  useEffect(() => {
    const revalidateSession = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) return;

      const now = Date.now();
      if (now - lastVisibilityCheckRef.current < VISIBILITY_RECHECK_DEBOUNCE_MS) {
        return;
      }
      lastVisibilityCheckRef.current = now;

      try {
        await axios.get(getApiUrl(API_ENDPOINTS.PROFILE));
      } catch (err: any) {
        // Interceptor already forceLogout on 401; clear for other auth failures too
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          forceLogout();
        }
      }
    };

    const onVisibleOrFocus = () => {
      if (document.visibilityState === 'visible') {
        void revalidateSession();
      }
    };

    document.addEventListener('visibilitychange', onVisibleOrFocus);
    window.addEventListener('focus', onVisibleOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibleOrFocus);
      window.removeEventListener('focus', onVisibleOrFocus);
    };
  }, [forceLogout]);

  // Fetch user permissions
  const fetchPermissions = async () => {
    const authToken = localStorage.getItem('token') || token;
    if (!authToken) {
      setPermissions([]);
      setScheduleViewDepartments([]);
      setCanViewAllScheduleDepartments(false);
      return;
    }

    try {
      const response = await axios.get(getApiUrl('/auth/permissions'), {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (response.data.success) {
        setPermissions(response.data.permissions || []);
        const viewAll = Boolean(response.data.canViewAllDepartments);
        setCanViewAllScheduleDepartments(viewAll);
        setScheduleViewDepartments(
          viewAll ? null : (response.data.scheduleViewDepartments || [])
        );
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions([]);
      setScheduleViewDepartments([]);
      setCanViewAllScheduleDepartments(false);
    }
  };

  // Check if user is authenticated on app load / token change
  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      if (token && isMounted) {
        try {
          console.log('🔐 Checking authentication...');
          const response = await axios.get(getApiUrl(API_ENDPOINTS.PROFILE));
          if (isMounted) {
            setUser(response.data.user);
            console.log('✅ Authentication successful');
            await fetchPermissions();
          }
        } catch (error) {
          console.error('❌ Auth check failed:', error);
          if (isMounted) {
            forceLogout();
          }
        }
      } else if (isMounted) {
        setUser(null);
        setPermissions([]);
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    // Small delay to prevent rapid successive requests
    const timeoutId = setTimeout(checkAuth, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [token, forceLogout]);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      const response = await axios.post(getApiUrl(API_ENDPOINTS.LOGIN), {
        email,
        password
      });

      const { token: newToken, user: userData, passwordResetInfo } = response.data;

      console.log('Login response:', {
        hasPasswordResetInfo: !!passwordResetInfo,
        userPasswordResetRequired: userData.passwordResetRequired
      });

      localStorage.setItem('token', newToken);
      applyAuthHeader(newToken);
      setToken(newToken);
      setUser(userData);

      await fetchPermissions();

      if (passwordResetInfo || userData.passwordResetRequired) {
        console.log('Setting passwordResetRequired flag in localStorage');
        localStorage.setItem('passwordResetRequired', 'true');
      }
    } catch (error: any) {
      console.error('Authentication error:', error);

      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        setError('Cannot connect to server. Please ensure the backend server is running on port 4000.');
      } else {
        setError(error.response?.data?.error || 'Login failed');
      }
      throw error;
    }
  };

  const register = async (userData: RegisterData): Promise<{ success: boolean; needsApproval: boolean }> => {
    try {
      setError(null);
      const response = await axios.post(getApiUrl(API_ENDPOINTS.REGISTER), userData);

      const { token: newToken, user: newUser } = response.data;

      if (newToken && newUser.approvalStatus === 'approved') {
        localStorage.setItem('token', newToken);
        applyAuthHeader(newToken);
        setToken(newToken);
        setUser(newUser);
        await fetchPermissions();
        return { success: true, needsApproval: false };
      }
      return { success: true, needsApproval: true };
    } catch (error: any) {
      setError(error.response?.data?.error || 'Registration failed');
      throw error;
    }
  };

  const logout = () => {
    forceLogout();
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(getApiUrl(API_ENDPOINTS.PROFILE));
        setUser(response.data.user);
        await fetchPermissions();
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }
    }
  };

  const refreshPermissions = async () => {
    await fetchPermissions();
  };

  const hasPermission = (permissionCode: string): boolean => {
    if (!permissions || permissions.length === 0) {
      return false;
    }

    if (permissions.includes('*.*')) {
      return true;
    }

    return permissions.includes(permissionCode);
  };

  const hasAnyPermission = (...permissionCodes: string[]): boolean => {
    return permissionCodes.some(code => hasPermission(code));
  };

  const setTokenFromServer = (newToken: string) => {
    if (!newToken) return;
    localStorage.setItem('token', newToken);
    applyAuthHeader(newToken);
    setToken(newToken);
  };

  const value: AuthContextType = {
    user,
    token,
    permissions,
    scheduleViewDepartments,
    canViewAllScheduleDepartments,
    login,
    register,
    logout,
    refreshUser,
    refreshPermissions,
    hasPermission,
    hasAnyPermission,
    loading,
    error,
    setTokenFromServer
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
